import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const TRANSPORT_SESSION_PATH = '/storefront-transport/session';
const TOKEN_REFRESH_WINDOW_MS = 60 * 1000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();
const hkdfInfo = encoder.encode('jlastore-storefront-transport-v1');

const transportClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 12 * 1000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let transportSession = null;
let transportSessionPromise = null;

const bytesToBase64Url = (value) => {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = '';

  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const base64UrlToBytes = (value) => {
  const normalized = String(value || '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '=',
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const buildAad = (direction, timestamp) =>
  encoder.encode(`jlastore:${direction}:v1:${timestamp}`);

const deriveEncryptionKey = async (token, salt) => {
  if (!window.crypto?.subtle) {
    throw new Error('Secure browser encryption is unavailable');
  }

  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(token),
    'HKDF',
    false,
    ['deriveKey'],
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt,
      info: hkdfInfo,
    },
    keyMaterial,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['encrypt', 'decrypt'],
  );
};

export const encryptTransportPayload = async (
  payload,
  token,
  direction = 'request',
) => {
  const timestamp = Date.now();
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveEncryptionKey(token, salt);
  const plaintext = encoder.encode(JSON.stringify(payload ?? null));
  const encrypted = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      additionalData: buildAad(direction, timestamp),
      tagLength: 128,
    },
    key,
    plaintext,
  );

  return {
    v: 1,
    alg: 'A256GCM',
    ts: timestamp,
    salt: bytesToBase64Url(salt),
    iv: bytesToBase64Url(iv),
    data: bytesToBase64Url(encrypted),
  };
};

export const decryptTransportPayload = async (
  envelope,
  token,
  direction = 'response',
) => {
  if (
    !envelope
    || envelope.v !== 1
    || envelope.alg !== 'A256GCM'
    || !envelope.salt
    || !envelope.iv
    || !envelope.data
  ) {
    throw new Error('Malformed encrypted server response');
  }

  const salt = base64UrlToBytes(envelope.salt);
  const iv = base64UrlToBytes(envelope.iv);
  const key = await deriveEncryptionKey(token, salt);
  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
      additionalData: buildAad(direction, Number(envelope.ts)),
      tagLength: 128,
    },
    key,
    base64UrlToBytes(envelope.data),
  );

  return JSON.parse(decoder.decode(decrypted));
};

const hasValidTransportSession = () =>
  Boolean(
    transportSession?.token
    && transportSession.expiresAt > Date.now() + TOKEN_REFRESH_WINDOW_MS,
  );

let transportSessionDisabledUntil = 0;
const SESSION_RETRY_BACKOFF_MS = 60 * 1000;

export const resetStorefrontTransportSession = () => {
  transportSession = null;
  transportSessionPromise = null;
  transportSessionDisabledUntil = 0;
};

export const getStorefrontTransportToken = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && hasValidTransportSession()) {
    return transportSession.token;
  }

  if (Date.now() < transportSessionDisabledUntil) {
    return null;
  }

  if (!forceRefresh && transportSessionPromise) {
    return transportSessionPromise;
  }

  transportSessionPromise = transportClient
    .post(TRANSPORT_SESSION_PATH)
    .then((response) => {
      const session = response.data?.data;

      if (!session?.token || !Number.isFinite(Number(session.expiresAt))) {
        throw new Error('Invalid storefront transport session');
      }

      transportSession = {
        token: session.token,
        expiresAt: Number(session.expiresAt),
      };

      return transportSession.token;
    })
    .catch((error) => {
      const status = error?.response?.status;
      console.warn(
        `[Storefront Transport] Session negotiation unavailable (status: ${status || 'network'}), falling back to standard transport`,
      );
      transportSessionDisabledUntil = Date.now() + SESSION_RETRY_BACKOFF_MS;
      return null;
    })
    .finally(() => {
      transportSessionPromise = null;
    });

  return transportSessionPromise;
};

const isBodyEncryptable = (body) => {
  if (body === undefined || body === null) return false;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return false;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return false;
  return true;
};

const setHeader = (headers, name, value) => {
  if (typeof headers?.set === 'function') {
    headers.set(name, value);
  } else {
    headers[name] = value;
  }
};

const deleteHeader = (headers, name) => {
  if (typeof headers?.delete === 'function') {
    headers.delete(name);
  } else {
    delete headers[name];
    delete headers[name.toLowerCase()];
  }
};

export const prepareStorefrontTransportRequest = async (config) => {
  if (config.storefrontEncryption === false) {
    return config;
  }

  let token = null;
  try {
    token = await getStorefrontTransportToken();
  } catch (err) {
    token = null;
  }

  if (!token) {
    // Graceful fallback to unencrypted standard request
    if (config.headers) {
      deleteHeader(config.headers, 'X-Storefront-JWT');
      deleteHeader(config.headers, 'X-Storefront-Encrypted');
    }
    delete config._storefrontTransportToken;
    return config;
  }

  config.headers = config.headers || {};
  config._storefrontTransportToken = token;
  setHeader(config.headers, 'X-Storefront-JWT', token);

  const method = String(config.method || 'get').toLowerCase();
  const originalBody = Object.prototype.hasOwnProperty.call(
    config,
    '_storefrontPlainData',
  )
    ? config._storefrontPlainData
    : config.data;

  if (!['get', 'head'].includes(method) && isBodyEncryptable(originalBody)) {
    config._storefrontPlainData = originalBody;
    config.data = await encryptTransportPayload(originalBody, token, 'request');
    setHeader(config.headers, 'X-Storefront-Encrypted', '1');
    setHeader(config.headers, 'Content-Type', 'application/json');
  } else {
    config.data = originalBody;
    deleteHeader(config.headers, 'X-Storefront-Encrypted');
  }

  return config;
};

const getResponseHeader = (response, name) => {
  const headers = response?.headers;
  if (!headers) return '';
  if (typeof headers.get === 'function') return headers.get(name) || '';
  return headers[name] || headers[name.toLowerCase()] || '';
};

export const decryptStorefrontTransportResponse = async (response) => {
  if (!response || getResponseHeader(response, 'x-storefront-encrypted') !== '1') {
    return response;
  }

  const token = response.config?._storefrontTransportToken;
  if (!token) {
    throw new Error('Missing storefront transport token for response');
  }

  response.data = await decryptTransportPayload(response.data, token, 'response');
  return response;
};

export const isStorefrontTransportTokenError = (response) =>
  getResponseHeader(response, 'x-storefront-transport-error') === 'invalid_token';

export const restoreStorefrontPlainRequest = (config = {}) => {
  if (Object.prototype.hasOwnProperty.call(config, '_storefrontPlainData')) {
    config.data = config._storefrontPlainData;
  }

  if (config.headers) {
    deleteHeader(config.headers, 'X-Storefront-JWT');
    deleteHeader(config.headers, 'X-Storefront-Encrypted');
  }

  delete config._storefrontTransportToken;
  return config;
};
